import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * Canonical visual style (border, height, radius, padding, font, text color)
 * for a mobile map-controls dropdown trigger — this is MapSelect's own "box"
 * variant look, extracted so WeatherMapControls' ChecklistDropdown can render
 * an identical-looking trigger (its `variant="unified"`) inside
 * UnifiedMapContainer's mobile "Map controls" panel, where the weather-layer
 * MapSelect and the Status/Events checklist dropdowns sit side by side and
 * must be visually indistinguishable. Deliberately excludes layout classes
 * (width/justify) since those depend on each consumer's own flex context.
 */
export const MAP_CONTROL_TRIGGER_CLASS =
  'min-h-11 rounded-lg border border-[#C3C4C3] bg-white px-3 text-xs text-[#282A29] dark:border-[#3a3a3a] dark:bg-[#1d1d1d] dark:text-[#f0f5f2]';

/**
 * Compact single-select dropdown for the map controls (weather layer, weight
 * unit). Styled to match the app — neutral border, light menu, green accent on
 * the active option — and layered at z-[1100] so the open menu is never hidden
 * under the Leaflet map (panes/controls sit at ~200-1000). Closes on outside
 * click and Escape.
 *
 * options: [{ value, label }]
 */
const MapSelect = ({ value, options = [], onSelect, className = '', ariaLabel, variant = 'box' }) => {
  const isInline = variant === 'inline';
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        className={
          isInline
            ? 'flex min-h-11 items-center gap-1 text-xs font-semibold text-[#282A29] dark:text-[#f0f5f2]'
            : `flex w-full items-center justify-between gap-2 ${MAP_CONTROL_TRIGGER_CLASS}`
        }
      >
        <span className={isInline ? '' : 'truncate'}>{selected ? selected.label : (value ?? '')}</span>
        <ChevronDown
          size={14}
          className={`shrink-0 text-[#828382] transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <ul
          role="listbox"
          className={`absolute top-full z-[1100] mt-1 overflow-hidden rounded-lg border border-[#C3C4C3] bg-white shadow-xl dark:border-[#5d625f] dark:bg-[#282a29] ${
            isInline ? 'right-0 min-w-[7rem]' : 'inset-x-0'
          }`}
        >
          {options.map((o) => (
            <li key={o.value}>
              <button
                type="button"
                role="option"
                aria-selected={o.value === value}
                onClick={() => {
                  onSelect(o.value);
                  setOpen(false);
                }}
                className={`flex min-h-11 w-full items-center px-3 py-2.5 text-left text-xs ${
                  o.value === value
                    ? 'bg-bloom-green/10 font-semibold text-bloom-green'
                    : 'text-[#282A29] hover:bg-[#E8EDEA] dark:text-[#f0f5f2] dark:hover:bg-[#232121]'
                }`}
              >
                {o.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default MapSelect;
